import { useState } from "react";
import type { HeldOrder, Table } from "@ai-pos/shared";
import { ClockIcon, PersonIcon, PlusIcon } from "../../components/icons.js";

interface Props {
  tables: Table[];
  heldOrderByTableId: Map<string, HeldOrder>;
  computeHeldOrderAmount: (heldOrder: HeldOrder) => number;
  onStart: (table: Table) => void;
  onResume: (heldOrder: HeldOrder) => Promise<void>;
}

const WARN_THRESHOLD_MINUTES = 60;

function elapsedMinutes(createdAt: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000));
}

function formatElapsed(minutes: number): string {
  if (minutes < 60) return `${minutes}分`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return `${hours}時間${remaining}分`;
}

export function TableSelectScreen({ tables, heldOrderByTableId, computeHeldOrderAmount, onStart, onResume }: Props) {
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleResume = async (heldOrder: HeldOrder) => {
    setError(null);
    try {
      await onResume(heldOrder);
    } catch (err) {
      setError(err instanceof Error ? err.message : "呼び出しに失敗しました");
    }
  };

  const selectedTable = tables.find((t) => t.id === selectedTableId);

  return (
    <div className="table-select">
      <div className="ts-heading">
        <h3>テーブルを選択してください</h3>
        <div className="legend">
          <span className="legend-item">
            <span className="dot free"></span>空席
          </span>
          <span className="legend-item">
            <span className="dot busy"></span>在席
          </span>
          <span className="legend-item">
            <span className="dot warn"></span>長時間在席
          </span>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="ts-grid">
        {tables.map((table) => {
          const heldOrder = heldOrderByTableId.get(table.id);
          if (heldOrder) {
            const minutes = elapsedMinutes(heldOrder.createdAt);
            const warn = minutes >= WARN_THRESHOLD_MINUTES;
            return (
              <button
                key={table.id}
                type="button"
                className={`table-card busy${warn ? " warn" : ""}`}
                onClick={() => void handleResume(heldOrder)}
              >
                <div className="tc-top">
                  <span className="tname">{table.name}</span>
                  <span className={`status-dot${warn ? " warn" : " busy"}`}></span>
                </div>
                <div className="tc-meta">
                  <span>
                    <ClockIcon />
                    {formatElapsed(minutes)}
                  </span>
                  <span>
                    <PersonIcon />
                    {heldOrder.guestCount}名
                  </span>
                </div>
                <div className="tc-amount num">¥{computeHeldOrderAmount(heldOrder).toLocaleString("ja-JP")}</div>
              </button>
            );
          }
          const selected = selectedTableId === table.id;
          return (
            <button
              key={table.id}
              type="button"
              className={`table-card free${selected ? " selected" : ""}`}
              onClick={() => setSelectedTableId(table.id)}
            >
              <span className="plus">
                <PlusIcon />
              </span>
              <span className="tname">{table.name}</span>
              <span className="hint">{selected ? "タップで開始・選択中" : "タップで開始"}</span>
            </button>
          );
        })}
        {tables.length === 0 && (
          <div className="empty-state">テーブルが登録されていません。「テーブル管理」画面から追加してください。</div>
        )}
      </div>

      <div className="ts-footer">
        <button
          className={`cta-start${selectedTable ? " ready" : ""}`}
          disabled={!selectedTable}
          onClick={() => {
            if (selectedTable) onStart(selectedTable);
          }}
        >
          {selectedTable ? `この席で開始する（${selectedTable.name}）` : "テーブルを選択してください"}
        </button>
      </div>
    </div>
  );
}
